<script setup>
import { getI18n } from '@/plugins/i18n'
import { ref, watch } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { getRouterPathFromCacheKey } from '@/router'

import * as dataGrid from '@/composables/data-grid'
import { useDateTimeColumn } from '@/composables/data-grid-column'

import { useOrderStore } from '@/stores/order.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import NotificationDetailsDialog from '@/components/shared/NotificationDetailsDialog.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'

import { getReferenceLinkText } from '@/services/entity-lock.service'

const gridId = 'notification-grid'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const orderStore = useOrderStore()

const markAsReadButton = ref(null)
const selectedRows = ref([])
const notificationDetailId = ref(null)

const { gridRef } = dataGrid.useGridRef()

const gridDataSource = dataGrid.useODataSource(
  webSiteStore.api.getAbsoluteUrl() + webSiteStore.api.notificationsUrl,
  {
    filter: ['created_by', '=', webSiteStore.user?.id]
  }
)

watch(() => webSiteStore.notificationsCount, () => gridRef.value.instance.refresh())
watch(() => webSiteStore.userDataFilter, () => gridRef.value.instance.refresh())

const onNotificationDetailClick = async(e, row) => {
  e.stopPropagation()
  const notification = row.data

  if (notification.type === webSiteStore.notificationTypesEnum.TRANSMISSION) {
    notificationDetailId.value = notification.id
  } else if (notification.type === webSiteStore.notificationTypesEnum.PRINT) {
    await webSiteStore.api.printReport({ fileUrl: notification.internal_message?.file_url, filename: notification.internal_message?.filename })
  } else await webSiteStore.downloadNotificationAsync(notification.id, `${i18n.t('common.notification')}_${notification.id}.txt`)
}

const notificationButtonIcon = (e) => e.data.type === webSiteStore.notificationTypesEnum.PRINT ? 'mdi-printer' : 'mdi-magnify'

const columns = [
  useDateTimeColumn({ sortOrder: 'desc' }),
  { dataField: 'is_read', caption: i18n.t('common.read'), width: '7rem', dataType: 'boolean', sortOrder: 'asc'},
  {
    type: 'buttons',
    buttons: [{
      hint: i18n.t('common.view'),
      template: 'detailButton',
      visible: (e) => [
        webSiteStore.notificationTypesEnum.INFO,
        webSiteStore.notificationTypesEnum.TRANSMISSION,
        webSiteStore.notificationTypesEnum.PRINT
      ].includes(e.row?.data?.type)
    }],
    width: '3rem',
    visibleIndex: 5,
    fixed: false,
  },
  {
    dataField: 'type',
    caption: i18n.t('common.type'),
    width: '10rem',
    lookup: dataGrid.useLookup({ store: webSiteStore.getLocalizedNotificationTypes(), displayExpr: 'name', sort: 'name' }),
  },
  {
    dataField: 'message',
    caption: i18n.t('common.message'),
    cellTemplate: 'entityLinkRef'
  },
  {
    dataField: 'transmission_status',
    width: '10rem',
    caption: i18n.t('common.transmissionStatus'),
    lookup: dataGrid.useLookup({ store: orderStore.getLocalizedOrderStatuses(), displayExpr: 'name' }),
    cellTemplate: 'transmissionStatusCellTemplate',
  },
  {
    dataField: 'transmission_order_number',
    caption: i18n.t('common.orderNumber'),
    visible: false,
  }
]

const onMarkAsRead = async() => {
  const data = selectedRows.value.map(row => ({ id: row.id, is_read: !row.is_read }))
  selectedRows.value.length === 0 ? await webSiteStore.updateNotificationsAsReadAsync([], { params: { all: true }}) : await webSiteStore.updateNotificationsAsReadAsync(data)
  selectedRows.value = []
  gridRef.value.instance.clearSelection()
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    {
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'mdi mdi-bell',
        text: selectedRows.value.length > 0 ? i18n.t('common.markAsRead') : i18n.t('common.markAllAsRead'),
        onClick: onMarkAsRead,
        onInitialized: (e) => { markAsReadButton.value = e.component }
      }
    },
  )

  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  markAsReadButton.value.option('text', selectedRows.value.length > 0 ? i18n.t('common.markAsRead') : i18n.t('common.markAllAsRead'))
}

const onEditingStart = async(e) => {
  e.promise = webSiteStore.fetchNotificationDetailsByIdAsync(e.data.id).then(res => {
    e.data.transmission_order_number = res.data.transmission_order_number
    e.data.transmission_order_date = res.data.transmission_order_date
    e.data.transmission_order_status = res.data.transmission_order_status
    e.data.message = res.data.message
    e.data.raw_message = res.data.raw_message
  })
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: {
    allowAdding: false,
    allowEditing: false,
    allowDeleting: false,
  },
  selection: dataGrid.useDefaultGridSelection(),
  onEditingStart: onEditingStart,
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
})

</script>
<template>
  <div>
    <notification-details-dialog
      v-if="notificationDetailId"
      :notification-id="notificationDetailId"
      @cancel="notificationDetailId = null"
    />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :gridExtRef="gridRef"
          :gridExtId="gridId"
        />
      </template>
      <template #detailButton="{ data: cellInfo }">
        <q-icon
          size="xs"
          class="dx-datagrid dx-link"
          :name="notificationButtonIcon(cellInfo)"
          @click="(e) => onNotificationDetailClick(e, cellInfo)"
        />
      </template>
      <template #transmissionStatusCellTemplate="{ data: templateOptions }">
        <q-chip
          v-if="!!templateOptions.data.transmission_status"
          size="sm"
          text-color="white"
          :label="orderStore.getLocalizedOrderStatuses().find(status => status.id === templateOptions.data.transmission_status)?.name"
          :color="templateOptions.data.transmission_status !== -1 ? 'positive' : 'negative'"
        />
      </template>
      <template #entityLinkRef="{ data: cellInfo }">
        <reference-link
          v-if="cellInfo.data.internal_message?.reference_link_data?.identifier_values"
          :data="{
            prefix:$t(cellInfo.data.internal_message.reference_link_data.text),
            ...Object.fromEntries(
              cellInfo.data.internal_message.reference_link_data.identifier_params.map((p, i) => [p, cellInfo.data.internal_message.reference_link_data.identifier_values[i]])
            )
          }"
          :text="getReferenceLinkText(cellInfo.data.internal_message)"
          :query-fields="[...cellInfo.data.internal_message.reference_link_data.identifier_params]"
          :target="getRouterPathFromCacheKey(cellInfo.data.internal_message)"
        />
        <span
          v-else
        >
          {{
            cellInfo.data.message
              || (
                cellInfo.data.internal_message?.translatable_message
                  ? i18n.t(
                    cellInfo.data.internal_message.translatable_message,
                    cellInfo.data.internal_message.message_params || {}
                  )
                  : ''
              )
          }}
        </span>
      </template>
    </dx-data-grid>
  </div>
</template>
